import React, { useState, useEffect } from 'react';
// import MUI components
import { Grid2 as Grid, Alert, Snackbar } from '@mui/material';
// import custom components
import { BoxCenterStart } from '../style/box';
import { TypographyBold } from '../style/typography';
import { commonStyles } from "../style/common";
import CommonCard from '../components/common_card';
import { BaseDialog } from '../components/base_dialog';
import SuiroExtractRequiredList from '../components/suiro_extract_required_list';
import SkeletonLoading from "../components/skeleton_loading";
import { fetchData, postData } from "../func/api";
import { useForm } from "react-hook-form";
import { REPORT_STATUS } from '../const/config';

const MstSuiroExtractRequiredList = () => {
    const [isSuiroExtractRequiredList, setIsSuiroExtractRequiredList] = useState([]);
    const [isSuiroExtractOptionalList, setIsSuiroExtractOptionalList] = useState([]);
    const [expandedPowerplant, setExpandedPowerplant] = useState(false);
    const [editPowerplant, setEditPowerplant] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogType, setDialogType] = useState(null);
    const [dialogData, setDialogData] = useState(null);
    const [alertMessage, setAlertMessage] = useState("");
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertSeverity, setAlertSeverity] = useState("success");
    const [refreshFlag, setRefreshFlag] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isDisabled, setIsDisabled] = useState(false);

    const { control: editControl, handleSubmit: handleEditSubmit, reset: editReset } = useForm({
        defaultValues: {
            editPowerplant: {
                name: "",
                is_suiro_extract_required: false
            }
        },
        mode: "onBlur"
    });

    // データ取得
    useEffect(() => {
        const fetchDataAll = async () => {
            try {
                const [isSuiroExtractRequiredList, isSuiroExtractOptionalList] = await Promise.all([
                    fetchData(`api/get_powerplant_suiro_extract_list/true`),
                    fetchData(`api/get_powerplant_suiro_extract_list/false`)
                ]);

                setIsSuiroExtractRequiredList(isSuiroExtractRequiredList); // 水路抜水必須発電所一覧
                setIsSuiroExtractOptionalList(isSuiroExtractOptionalList); // 水路抜水任意発電所一覧

                // 地震一覧データ取得
                const earthquakeData = await fetchData(`api/get_earthquake_list/`);
                // 業務対中の場合は、編集不可
                const reportingData = earthquakeData.find(obj =>
                    obj.report_status_uuid === REPORT_STATUS.UNSUPPORTED ||
                    obj.report_status_uuid === REPORT_STATUS.FISRTREPORT ||
                    obj.report_status_uuid === REPORT_STATUS.PRIMARYCHECK ||
                    obj.report_powerplant_status_uuid === REPORT_STATUS.POWERPLANTSUPPORT
                );
                setIsDisabled(reportingData ? true : false);
            } catch (error) {
                console.error("水路抜水管理一覧取得中にエラーが発生しました:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchDataAll();
    }, [refreshFlag]);

    // 編集対象の発電所が変わったらフォームをリセット
    useEffect(() => {
        if (editPowerplant) {
            editReset({
                editPowerplant: {
                    uuid: editPowerplant.uuid,
                    name: editPowerplant.name,
                    is_suiro_extract_required: editPowerplant.is_suiro_extract_required
                }
            });
        }
    }, [editPowerplant, editReset]);

    const openDialog = (type, data = null) => {
        setDialogType(type);
        setDialogData(data);
        setDialogOpen(true);
    };

    const handleCancelEdit = () => {
        setExpandedPowerplant(false);
        setEditPowerplant(null);
    };

    const handleDialogConfirm = () => {
        switch (dialogType) {
            case 'save':
                handleSaveConfirm(); // 保存処理
                break;
            default:
                break;
        }
        setDialogOpen(false);
    };

    const handleDialogCancel = () => {
        setDialogOpen(false);
    };

    const handleEditClick = (powerplant) => {
        setEditPowerplant({ ...powerplant });
        setExpandedPowerplant(powerplant.uuid);
    };

    const handleSaveConfirm = async () => {
        try {
            const updatedData = {
                ...dialogData
            };

            // --- 対象発電所の水路抜水必須を更新 ---
            await postData("api/post_update_powerplant_suiro_extract_required", updatedData);

            setAlertMessage("水路抜水必須管理の更新が完了しました。");
            setAlertSeverity("success");
            setAlertOpen(true);
            setRefreshFlag(prev => !prev);
        } catch (err) {
            console.error("更新失敗しました：", err);
            setAlertMessage("水路抜水必須管理の更新に失敗しました。");
            setAlertSeverity("error");
            setAlertOpen(true);
        } finally {
            setExpandedPowerplant(false);
            setEditPowerplant(null);
            setDialogOpen(false);
        }
    };

    if (loading) {
        return <SkeletonLoading />;
    }

    return (
        <BoxCenterStart sx={commonStyles.page}>
            <Grid
                container
                spacing={7}
                columns={12}
                sx={commonStyles.container}
            >
                {/* Page Title */}
                <Grid size={{ xs: 12 }}>
                    <CommonCard
                        size={{ xs: 12 }}
                        child={
                            <TypographyBold
                                sx={{ textAlign: 'center', padding: '10px 0' }}
                            >
                                発電所管理(水路抜水)
                            </TypographyBold>
                        }
                    ></CommonCard>
                </Grid>
                {/* 水路抜水必須発電所一覧 */}
                <SuiroExtractRequiredList
                    suiroExtractRequiredList={isSuiroExtractRequiredList}
                    control={editControl}
                    expandedPowerplant={expandedPowerplant}
                    handleEditClick={handleEditClick}
                    handleCancelEdit={handleCancelEdit}
                    handleEditSubmit={handleEditSubmit}
                    openDialog={openDialog}
                    title="水路抜水必須一覧"
                    isDisabled={isDisabled}
                />
                {/* 水路抜水任意発電所一覧 */}
                <SuiroExtractRequiredList
                    suiroExtractRequiredList={isSuiroExtractOptionalList}
                    control={editControl}
                    expandedPowerplant={expandedPowerplant}
                    handleEditClick={handleEditClick}
                    handleCancelEdit={handleCancelEdit}
                    handleEditSubmit={handleEditSubmit}
                    openDialog={openDialog}
                    title="水路抜水任意一覧"
                    isDisabled={isDisabled}
                />
                {/* 確認ダイアログ */}
                <BaseDialog
                    open={dialogOpen}
                    onClose={handleDialogCancel}
                    title={
                        dialogType === "save" ? "水路抜水必須管理保存" : ""
                    }
                    message={
                        dialogType === "save" ? "編集内容を保存します。よろしいですか？" : ""
                    }
                    onConfirm={handleDialogConfirm}
                    confirmText={
                        dialogType === "save" ? "保存" : "OK"
                    }
                    cancelText="キャンセル"
                />
                {/* 失敗時のSnackbar */}
                {alertMessage && (
                    <Snackbar
                        open={alertOpen}
                        autoHideDuration={3000}
                        onClose={() => setAlertOpen(false)}
                        anchorOrigin={{ vertical: "top", horizontal: "center" }}
                    >
                        <Alert severity={alertSeverity} onClose={() => setAlertOpen(false)}>
                            {alertMessage}
                        </Alert>
                    </Snackbar>
                )}
            </Grid>
        </BoxCenterStart>
    );
};

export default MstSuiroExtractRequiredList;